import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import { Modal, Button, Spinner } from 'react-bootstrap';
import IncidentCard from './IncidentCard';
import { FaChevronDown, FaChevronUp, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { TbArrowsSort } from 'react-icons/tb';
import { MdDragIndicator } from 'react-icons/md';
import AuthContext from '../../../auth/AuthContext';
import CatalogueService from '../../../services/CatalogueService';
import AlertService from '../../../services/AlertService';
import TimerService from '../../../services/TimerService';
import View from './View';
import ConfirmDialogBox from '../../confirmDialog/ConfirmDialogBox';
import { ThemeContext } from '../../../theme/ThemeContext';
import SharedService from '../../../services/SharedService';
import { HiOutlineFilter } from 'react-icons/hi';

const CategoryDetail = () => {
  const { categoryId, courseId: paramCourseId } = useParams();
  const { state } = useLocation();
  const courseId = state?.courseId || state?.id || paramCourseId;
  const isCourseView = !!courseId;
  const navigate = useNavigate();
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [groupedIncidents, setGroupedIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryTitle, setCategoryTitle] = useState('');
  const [incidentsData, setIncidentsData] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [startTime, setStartTime] = useState(null);
  const authContext = useContext(AuthContext);
  const timeSpentRef = useRef();
  const catalogueAccess = authContext.getPermissions('catalogues');
  const showAdd = catalogueAccess.some(f => f.access.includes(2));
  const showEdit = catalogueAccess.some(f => f.access.includes(3));
  const showDelete = catalogueAccess.some(f => f.access.includes(4));
  const activeIncidentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isAllCategory = !categoryId;
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const [searchText, setSearchText] = useState('');
  const cardAbortRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  //Reorder Modal State
  const [reorderModal, setReorderModal] = useState({
    show: false,
    type: null,       // 'category' | 'incident'
    parentKey: null,
    childKey: null,
    list: [],
  });
  const [reorderSaving, setReorderSaving] = useState(false);
  const reorderDragItem = useRef(null);
  const reorderDragOver = useRef(null);

  // Debounce Search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Group Incidents
  const groupIncidents = useCallback((incidents) => {
    const parentGroups = Object.groupBy(incidents, ({ parentCategory }) => parentCategory || '');
    return Object.entries(parentGroups).map(([parent, parentItems]) => {
      const childGroups = Object.groupBy(parentItems, ({ category }) => category || '');
      const children = Object.entries(childGroups).map(([child, childItems]) => ({
        key: child,
        visible: true,
        items: childItems,
      }));
      return {
         key: parent, 
         items: children,
        };
      });
  }, []);

// Extract available categories from grouped incidents
  useEffect(() => {
    if (groupedIncidents.length > 0) {
      const categories = new Set();
      groupedIncidents.forEach(parentGroup => {
        parentGroup.items.forEach(childGroup => {
          if (childGroup.key) {
            categories.add(childGroup.key);
          }
        });
      });
      setAvailableCategories(['All', ...Array.from(categories).sort()]);
    }
  }, [groupedIncidents]);

// Filter incidents based on selected categories
  const filterIncidents = (incidentsList) => {
    let filtered = [...incidentsList];
    if (isAllCategory && !selectedCategories.includes('All') && selectedCategories.length > 0) {
      filtered = filtered.filter(i => selectedCategories.includes(i.category));
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase().trim();
      filtered = filtered.filter(i =>
        (i.incidentTitle || '').toLowerCase().includes(q) ||
        (i.incidentDescription || '').toLowerCase().includes(q) ||
        (i.keywords || []).join(' ').toLowerCase().includes(q)
        // (i.category || '').toLowerCase().includes(q) ||
        // (i.parentCategory || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  };

 // Toggle category selection
  const toggleCategory = (category) => {
    if (category === 'All') {
      setSelectedCategories(['All']);
    } else {
      setSelectedCategories(prev => {
        const newSelection = prev.includes(category)
          ? prev.filter(c => c !== category)
          : [...prev.filter(c => c !== 'All'), category];

        return newSelection.length === 0 ? ['All'] : newSelection;
      });
    }
  };

// Filter categories based on search
  const filteredCategories = availableCategories.filter(category =>
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

useEffect(() => {
  if (selectedIncident && !selectedIncident.isLoading) {
    setStartTime(Date.now());
  }
}, [selectedIncident]);

useEffect(() => {
  if (!selectedIncident || !startTime) return;

  const interval = setInterval(() => {
    timeSpentRef.current = Math.floor((Date.now() - startTime) / 1000);
  }, 1000);

  return () => clearInterval(interval);
}, [selectedIncident, startTime]);

useEffect(() => {
  if (!selectedIncident) return;

  const currentCourseId = courseId;
  const currentIncidentId = selectedIncident.id;

  return () => {
    if (currentIncidentId && timeSpentRef.current > 0) {
      TimerService.Save(
        {
          courseId: currentCourseId,
          incidentId: currentIncidentId,
          timeSpent: timeSpentRef.current,
        },
        authContext
      );
      timeSpentRef.current = 0; 
    }
  };
}, [selectedIncident, courseId, authContext]);

  useEffect(() => {
    if (activeIncidentRef.current && scrollContainerRef.current) {
      setTimeout(() => {
        const item = activeIncidentRef.current;
        const container = scrollContainerRef.current;

        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const isVisible =
          itemRect.top >= containerRect.top &&
          itemRect.bottom <= containerRect.bottom &&
          itemRect.left >= containerRect.left &&
          itemRect.right <= containerRect.right;

        if (!isVisible) {
          item.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 100);
    }
  }, [selectedIncident]);

const updateTimeSpent = useCallback(async () => {
  if (selectedIncident?.id && timeSpentRef.current > 0) { 
    const payload = {
      courseId,
      incidentId: selectedIncident.id,
      timeSpent: timeSpentRef.current,
    };
    await TimerService.Save(payload, authContext);
    timeSpentRef.current = 0; 
  }
}, [authContext, courseId, selectedIncident]);

  const toggleChildVisibility = (parentKey, childKey) => {
    const updated = groupedIncidents.map(parent => {
      if (parent.key !== parentKey) return parent;
      return {
        ...parent,
        items: parent.items.map(child => {
          if (child.key !== childKey) return child;
          return { ...child, visible: !child.visible };
        }),
      };
    });
    setGroupedIncidents(updated);
  };

 const loadIncident = useCallback(
    async (cardId) => {
      const existing = incidentsData.find(f => f.id === cardId);
      if (existing) {
        setSelectedIncident(existing);
      } else {
        setSelectedIncident({ id: cardId, isLoading: true });
      }

      setLoading(true);

      if (selectedIncident?.id) {
        updateTimeSpent();
      }

      if (existing) {
        setLoading(false);
      } else {
        if (cardAbortRef.current) {
          cardAbortRef.current.abort();
        }
        const controller = new AbortController();
        cardAbortRef.current = controller;

        try {
          const data = await CatalogueService.Get(
            cardId,
            authContext,
            controller.signal
          );

          if (controller.signal.aborted) return; 

          if (data.isSuccess) {
            setIncidentsData(prev =>
              prev.find(f => f.id === data.data.id) ? prev : [...prev, data.data],
            );
            setSelectedIncident(data.data);
          } else {
            AlertService.error(data.errorMessage);
          }
        } catch (err) {
          if (err.name === "AbortError") return;
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      }
    },
    [authContext, incidentsData, updateTimeSpent, selectedIncident],
  );

 const loadIncidents = useCallback(async (signal) => { 
    setLoading(true);
    try {
      let data;
      if (isAllCategory && courseId) {
        data = await CatalogueService.GetAllByCourseId(
          courseId, '', authContext, signal
        );
        if (signal?.aborted) return;
        if (data.isSuccess) {
          setGroupedIncidents(groupIncidents(data.data.incidents));
          setCourseName(data.data.course.courseName);
        }
      } else if (isAllCategory) {
        data = await CatalogueService.GetAll('', authContext, signal);
        if (signal?.aborted) return;
        if (data.isSuccess) {
          setGroupedIncidents(groupIncidents(data.data));
        }
      } else {
        const catId = state?.value || categoryId;
        if (!catId) {
          AlertService.error('Invalid category ID');
          navigate('/catalogue-dashboard/catalogue');
          return;
        }
        data = await CatalogueService.GetAllByCategoryId(
          catId, '', authContext, signal
        );
        if (signal?.aborted) return;
        if (data.isSuccess) {
          const title =
            data.data.category?.categoryName || state?.title || categoryId.replace(/-/g, ' ');
          setCategoryTitle(title);
          setGroupedIncidents(groupIncidents(data.data.incidents));
        }
      }

      if (data && !data.isSuccess) {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      AlertService.error('Failed to load incidents.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext, groupIncidents, isAllCategory, state, categoryId, courseId, navigate]);

useEffect(() => {
    const controller = new AbortController();
    loadIncidents(controller.signal);
        return () => {
      controller.abort();
      cardAbortRef.current?.abort();
    };

  }, [loadIncidents]);

  const handleCardClick = (incident) => {
    if (incident?.id) {
      loadIncident(incident.id);
    }
  };

  const handleAddIncident = () => navigate('/catalogue-dashboard/catalogue/add');
  const handleEditIncident = () => {
      if (selectedIncident?.isLoading) return;
    const slug = SharedService.GenerateSlug(selectedIncident.incidentTitle);
    navigate(`/catalogue-dashboard/catalogue/edit/${slug}`, { state: { id: selectedIncident.id } });
  };
  const onDeleteHandler = async () => {
    setShowDeleteConfirm(false);
    const response = await CatalogueService.Remove(selectedIncident.id, authContext);

    if (response.isSuccess) {
      AlertService.success('Incident deleted successfully!');
      setSelectedIncident(null);
      loadIncidents();
    } else {
      AlertService.error(response.errorMessage);
    }
  };

  const statusHandler = (newStatus) => {
    setSelectedIncident(prevIncident => {
      if (!prevIncident) return null;
      return { ...prevIncident, status: newStatus };
    });

    setGroupedIncidents(prevGroups =>
      prevGroups.map(group => ({
        ...group,
        items: group.items.map(subgroup => ({
          ...subgroup,
          items: subgroup.items.map(incident =>
            incident.id === selectedIncident?.id ? { ...incident, status: newStatus } : incident,
          ),
        })),
      })),
    );

    setIncidentsData(prevData =>
      prevData.map(incident =>
        incident.id === selectedIncident?.id ? { ...incident, status: newStatus } : incident,
      ),
    );
  };

const handleStatusConfirm = async () => {
  const newStatus = selectedIncident?.status === 2 ? 0 : 2;
   try {
    await updateTimeSpent();
    if (newStatus === 2) {
      await CatalogueService.MarkAsCompleted(selectedIncident.id, authContext);
      AlertService.success("Marked as Completed successfully");
    } else {
      await CatalogueService.MarkAsIncomplete(selectedIncident.id, authContext);
      AlertService.success("Marked as Incomplete successfully");
    }
    setSelectedIncident(prev => ({ ...prev, status: newStatus }));
    statusHandler(newStatus);
  } catch {
    AlertService.error('Failed to update status');
  } finally {
    setShowStatusDialog(false);
  }
};

  
  const hasAnyVisibleIncident = () => {
    for (const parentGroup of groupedIncidents) {
      for (const childGroup of parentGroup.items) {
        const filteredItems = filterIncidents(childGroup.items);
        if (filteredItems.length > 0) {
          return true;
        }
      }
    }
    return false;
  };

 //Reorder: Open Modal
const openCategoryReorder = (e, parentKey) => {
  e.stopPropagation();
  const parentGroup = groupedIncidents.find(p => p.key === parentKey);
  if (!parentGroup) return;

  const parentId = parentGroup.items[0]?.items[0]?.parentCategoryId; 

  setReorderModal({
    show: true,
    type: 'category',
    parentKey,
    parentId, 
    childKey: null,
    list: parentGroup.items.map(c => ({
      value: c.items[0]?.categoryId,
      label: c.key,
      key: c.key
    })),
  });
};

  // Open incident reorder 
const openIncidentReorder = (e, parentKey, childKey) => {
  e.stopPropagation();
  const parentGroup = groupedIncidents.find(p => p.key === parentKey);
  const childGroup = parentGroup?.items.find(c => c.key === childKey);
  if (!childGroup) return;

  const categoryId = childGroup.items[0]?.categoryId; 

  setReorderModal({
    show: true,
    type: 'incident',
    parentKey,
    childKey,
    categoryId, 
    list: childGroup.items.map(i => ({
      id: i.id,
      label: i.incidentTitle
    })),
  });
};

  const closeReorderModal = () => {
    if (reorderSaving) return;
    setReorderModal({ show: false, type: null, parentKey: null, childKey: null, list: [] });
  };

  // Reorder: Drag Handlers
  const handleReorderDragStart = (index) => { reorderDragItem.current = index; };
  const handleReorderDragEnter = (index) => { reorderDragOver.current = index; };
  const handleReorderDragEnd = () => {
    if (reorderDragItem.current === null || reorderDragOver.current === null) return;
    const updated = [...reorderModal.list];
    const dragged = updated.splice(reorderDragItem.current, 1)[0];
    updated.splice(reorderDragOver.current, 0, dragged);
    reorderDragItem.current = null;
    reorderDragOver.current = null;
    setReorderModal(prev => ({ ...prev, list: updated }));
  };

  // Reorder: Save 
  const handleReorderSave = async () => {
    setReorderSaving(true);
    try {
      if (reorderModal.type === 'category') {
      // Payload
      const payload = reorderModal.list.map((item, index) => ({
      parentId: reorderModal.parentId, 
      childId: item.value,
      orderBy: index + 1,
      }));
        const result = await CatalogueService.SaveChildCategoriesOrder(authContext, payload);
        if (result.isSuccess) {
          setGroupedIncidents(prev => prev.map(p => {
            if (p.key !== reorderModal.parentKey) return p;
            const reordered = reorderModal.list.map(item => p.items.find(c => c.key === item.key)).filter(Boolean);
            return { ...p, items: reordered };
          }));
          AlertService.success('Category order saved!');
          closeReorderModal();
            await loadIncidents(); 
        } else {
          AlertService.error(result.errorMessage);
        }
      } else {
        // Payload: incident 
        const payload = reorderModal.list.map((item, index) => ({
        incidentId: item.id,
        categoryId: reorderModal.categoryId,
        orderBy: index + 1,
        }));
        const result = await CatalogueService.SaveIncidentsOrder(authContext, payload);
        if (result.isSuccess) {
          setGroupedIncidents(prev => prev.map(p => {
            if (p.key !== reorderModal.parentKey) return p;
            return {
              ...p,
              items: p.items.map(c => {
                if (c.key !== reorderModal.childKey) return c;
                const reordered = reorderModal.list.map(item => c.items.find(i => i.id === item.id)).filter(Boolean);
                return { ...c, items: reordered };
              }),
            };
          }));
          AlertService.success('Incident order saved!');
          closeReorderModal();
          await loadIncidents();
        } else {
          AlertService.error(result.errorMessage);
        }
      }
    } catch {
      AlertService.error('Failed to save order. Please try again.');
    } finally {
      setReorderSaving(false);
    }
  };
  return (
    <div className="container-fluid custom-container">
      {!selectedIncident && (
        <div className="d-flex justify-content-end gap-3 mt-2 mb-3">
          {/* Search Box*/}
          <div className="position-relative" style={{ maxWidth: '230px' }}>
            <input
              type="text"
              className="form-control custom-input-color py-2 pe-4"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="btn position-absolute end-0 top-50 translate-middle-y fw-bold"
                onClick={() => setSearchText('')}
                style={{ zIndex: 10, right: '8px' }}
              >
                ✕
              </button>
            )}
          </div>

          {!isAllCategory && (
            <button 
            className="btn btn-outline py-2 px-3 d-flex align-items-center gap-2"
              onClick={() => navigate('/catalogue-dashboard/catalogue/all')}
              >
              <i className="pi pi-eye" /> View All Categories
            </button>
          )}

          {/* Filter Button */}
          {isAllCategory && (
            <div className="position-relative">
              <button 
              className="btn btn-outline py-2 px-3 d-flex align-items-center gap-2"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
                <HiOutlineFilter /> Filter Categories
              </button>
              {showFilterDropdown && (
                <div 
                className={`position-absolute top-100 end-0 mt-2 z-3 border rounded shadow scroll-no-buttons dropdown`}
                  style={{ minWidth: '340px', maxHeight: '750px', overflow: 'auto' }}>
                  <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
                    <input type="text"
                      className={`form-control custom-input-color ${isDark ? 'bg-dark text-primary border-secondary' : ''}`}
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)} />
                    <button className="btn btn-sm btn-outline-secondary border pt-2 ms-2"
                      onClick={() => setShowFilterDropdown(false)}>
                      <i className="pi pi-times" />
                    </button>
                  </div>

                 {/* Category List */}
                  <div className="scroll-no-buttons" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {filteredCategories.map(category => (
                      <div key={category} className="px-3 py-2">
                        <div className="form-check custom-checkbox">
                          <input className="form-check-input" type="checkbox"
                            checked={selectedCategories.includes(category)}
                            onChange={() => toggleCategory(category)}
                            id={`filter-${category}`} />
                          <label 
                          className="form-check-label w-100 ms-2 pt-1"
                            htmlFor={`filter-${category}`} 
                            style={{ cursor: 'pointer' }}
                            >
                            {category}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-2 border-top d-flex gap-2">
                    <button 
                    className="btn btn-sm btn-outline-secondary w-100 py-2"
                      onClick={() => setSelectedCategories(['All'])}
                      >
                        Clear Filters
                        </button>
                    <button 
                    className="btn btn-sm btn-outline-secondary w-100 py-2"
                      onClick={() => setShowFilterDropdown(false)}
                      >
                        Cancel
                      </button>
                  </div>
                </div>
              )}

            </div>
          )}


          {showAdd && (
            <button className="btn btn-primary py-2" onClick={handleAddIncident}>
              <FaPlus className="pb-1 me-1" /> 
              Add New Incident
            </button>
          )}
        </div>
      )}

     {/* Close dropdown when clicking outside */}
      {showFilterDropdown && (
        <div 
        className="position-fixed top-0 left-0 w-100 h-100 z-2"
          onClick={() => setShowFilterDropdown(false)}
          style={{ background: 'transparent' }} 
          />
      )}

      {!selectedIncident ? (
        loading ? (
          <div className="row mt-4">
            {[...Array(8)].map((_, i) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={i}>
                <div className="shadow-sm border-0 rounded-3 p-3" style={{ minHeight: '120px' }}>
                  <Skeleton width="60%" height="1.5rem" className="mb-2" />
                  <Skeleton width="30%" height="1rem" className="mb-2" />
                  <Skeleton width="80%" height="1.25rem" />
                </div>
              </div>
            ))}
          </div>
       ) : hasAnyVisibleIncident() ? (
          groupedIncidents.map(parentGroup => {
            const filteredChildren = parentGroup.items
              .map(childGroup => ({
                ...childGroup,
                items: filterIncidents(childGroup.items),
              }))
              .filter(childGroup => childGroup.items.length > 0);

            if (filteredChildren.length === 0) return null;

            return (
              <div key={parentGroup.key} className="mb-5">
                {/* Parent Category Title + Category Reorder Button */}
                <div className="d-flex justify-content-center align-items-center gap-2 mb-4">
                  <h3 className="text-primary mb-0">{parentGroup.key || ''}</h3>
                 {showEdit && filteredChildren.length > 1 && !isCourseView && (
                    <button
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                      title="Reorder Child Categories"
                      onClick={(e) => openCategoryReorder(e, parentGroup.key)}
                    >
                      <TbArrowsSort size={16} />
                    </button>
                  )}
                </div>

                {filteredChildren.map(childGroup => (
                  <div key={childGroup.key} className="mb-4">
                    {/* {parentGroup.key !== childGroup.key && (  */}
                    <div
                      className={`position-relative p-3 mb-3 rounded cursor-pointer ${isDark ? 'bg-dark border border-dark text-light' : 'bg-light border text-dark'}`}
                      onClick={() => toggleChildVisibility(parentGroup.key, childGroup.key)}
                    >
                      <div className="d-flex justify-content-start align-items-center">
                        <span className="position-absolute fw-bold">{childGroup.key || 'No Subcategory'}</span>
                        <div className="ms-auto d-flex align-items-center gap-2">
                          {/* Incident Reorder Icon — stop propagation so collapse doesn't toggle */}
                         {showEdit && childGroup.items.length > 1 && !isCourseView && (
                            <button
                              className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                              title="Reorder Incidents"
                              onClick={(e) => openIncidentReorder(e, parentGroup.key, childGroup.key)}
                            >
                              <TbArrowsSort size={16} />
                            </button>
                          )}
                          <div
                            className={`d-flex align-items-center justify-content-center rounded-circle border ${isDark ? 'bg-dark border-light text-white' : 'bg-white border text-dark'}`}
                            style={{ width: '28px', height: '28px' }}
                          >
                            {childGroup.visible ? <FaChevronUp className="fs-6" /> : <FaChevronDown className="fs-6" />}
                          </div>
                        </div>
                      </div>
                    </div>
                     {/* )} */}

                    {childGroup.visible && (
                      <div className="row scroll-no-buttons" ref={scrollContainerRef}>
                        {childGroup.items.map((incident, k) => {
                          const isActive = selectedIncident?.id === incident.id;
                          
                          return (
                            <div 
                           key={`${incident.id}-${childGroup.key}-${k}`}
                              className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                              onClick={() => handleCardClick(incident)}
                              style={{ cursor: 'pointer' }}
                              ref={isActive ? activeIncidentRef : null}
                              >
                              <IncidentCard incident={incident} no={k + 1} isActive={isActive} status={incident.status} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <div className="text-center py-5 text-muted">No incidents found</div>
        )
      ) : (
        <div className="row g-2">
          <div className="d-flex justify-content-end gap-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <label htmlFor="statusCheck"><strong>Status:</strong></label>
              <input 
              className="form-check-input mb-1"
              type="checkbox"
              style={{ width: '20px', height: '20px', border: '2px solid #000' }}
              id="statusCheck"
              checked={selectedIncident?.status === 2}
              onChange={() => setShowStatusDialog(true)} 
              />
              <span>{selectedIncident?.status === 2 ? 'Completed' : 'Yet to Complete'}</span>
            </div>
            {showDelete && (
              <button className="btn text-danger btn-sm px-1 py-1" onClick={() => {
  if (selectedIncident?.isLoading) return;
  setShowDeleteConfirm(true);
}}

disabled={selectedIncident?.isLoading || loading}>
                <FaTrash className="me-1 ms-2 mb-1 fs-6" />
                 Delete
              </button>
            )}
            {showEdit && (
              <button className="btn text-primary btn-sm px-1 py-1" onClick={handleEditIncident}   disabled={selectedIncident?.isLoading || loading}>
                <FaEdit className="me-1 mb-1 fs-6" />
                Edit
              </button>
            )}
          </div>
          <div className="col-md-3 col-lg-2">
            <div 
            className="pe-2 scroll-no-buttons" 
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
             ref={scrollContainerRef}
             >
              {groupedIncidents.map(parentGroup => {
                const filteredChildren = parentGroup.items
                  .map(childGroup => ({
                    ...childGroup,
                    items: filterIncidents(childGroup.items),
                  }))
                  .filter(childGroup => childGroup.items.length > 0);

                if (filteredChildren.length === 0) return null;

                return (
                  <div key={parentGroup.key}>
                    {filteredChildren.map(childGroup => (
                      <div key={childGroup.key}>
                        <h6 className="text-center fw-semibold mb-2">{childGroup.key || ''}</h6>
                        {childGroup.items.map((incident, k) => {
                          const isActive = selectedIncident?.id === incident.id;
                          return (
                            <div key={`${incident.id}-${childGroup.key}-${k}`} ref={isActive ? activeIncidentRef : null}>
                              <IncidentCard
                                incident={incident}
                                no={k + 1}
                                clickHandler={handleCardClick}
                                isActive={isActive}
                                compact
                                status={incident.status}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
          {selectedIncident?.isLoading ? (
            <>
              <div className="col-12 col-md-8 mb-4">
                <div 
                className="border rounded p-2 h-100 d-flex flex-column scroll-no-buttons"
                  style={{ minHeight: '80vh', maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}
                  >
                  <div className="mb-3 mt-2">
                    <Skeleton width="30%" height="2rem" className="mb-2" />
                    <Skeleton width="60%" height="3rem" className="mb-2" />
                  </div>
                  <div className="mb-3">
                    <Skeleton width="30%" height="2rem" className="mb-2" />
                    <Skeleton width="70%" height="3rem" className="mb-2" />
                  </div>
                  <div className="mb-3">
                    <Skeleton width="30%" height="2rem" className="mb-2" />
                    <Skeleton width="90%" height="5rem" />
                  </div>
                  <div>
                    <Skeleton width="30%" height="2rem" className="mb-2" />
                    {[...Array(1)].map((_, i) => (
                      <Skeleton key={i} width="100%" height="20rem" className="mb-3" />
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-2 mb-4">
                <div className="border rounded p-2 h-100 scroll-no-buttons"
                  style={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden' }}>
                  {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} width="100%" height="5.6rem" className="mb-3 mt-2" />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <View
              incident={selectedIncident}
              courseName={courseName || categoryTitle}
              statusHandler={statusHandler}
            />
          )}
        </div>
      )}

      {/* Reorder Modal */}
      <Modal
        show={reorderModal.show}
        onHide={closeReorderModal}
        centered
        size="lg"
        backdrop={reorderSaving ? 'static' : true}
        keyboard={!reorderSaving}
      >
        <Modal.Header closeButton={!reorderSaving}>
          <Modal.Title>
            {reorderModal.type === 'category'
              ? `Reorder Child Categories — ${reorderModal.parentKey}`
              : `Reorder Incidents — ${reorderModal.childKey}`}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <p className=" small mb-3">
            Drag and drop to reorder. Click <strong>Save Order</strong> to apply.
          </p>
          {reorderModal.list.map((item, index) => (
            <div
              key={item.id ?? item.value ?? item.key}
              draggable
              onDragStart={() => handleReorderDragStart(index)}
              onDragEnter={() => handleReorderDragEnter(index)}
              onDragEnd={handleReorderDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className="d-flex align-items-center gap-2 p-2 ps-4 mb-2 rounded border custom-input-color"
              style={{ cursor: 'grab', userSelect: 'none', transition: 'box-shadow 0.15s ease' }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)')}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
            >
              <span className="font-semibold " style={{ minWidth: '24px', fontSize: '13px' }}>
                {index + 1}
              </span>
              <MdDragIndicator size={20} className="text-muted" />
              <span style={{ fontSize: '14px' }}>{item.label}</span>
            </div>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeReorderModal} disabled={reorderSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleReorderSave} disabled={reorderSaving}>
            {reorderSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving...</>
            ) : 'Save Order'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDialogBox 
      visible={showDeleteConfirm} 
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={onDeleteHandler} 
        title={<>Are you sure you want to delete this incident?</>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel" 
        />
      <ConfirmDialogBox 
      visible={showStatusDialog} 
      onHide={() => setShowStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        title={
        <>
        Are you sure you want to mark this as{' '}
          <strong>{selectedIncident?.status === 2 ? 'Incomplete' : 'Completed'}</strong>?
          </>
        }
        confirmLabel="Yes, Confirm"
         cancelLabel="Cancel" 
         />
    </div>
  );
};

export default CategoryDetail;